import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Route, Wrench, MapPin } from 'lucide-react';
import { fetchGoogleMapsBrowserKey } from '../../technician-panel/services/technicianPanelApi';
import { loadGoogleMaps } from '../../technician-panel/utils/technicianPanelGoogleMaps';
import './CheckoutViewModal.css';

const TRAVEL_COLOR = '#6D54E8';

function sortPointsByTime(points) {
  return [...(points || [])]
    .filter((p) => p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)))
    .sort((a, b) => String(a.recordedAt || '').localeCompare(String(b.recordedAt || '')));
}

function buildTravelPaths(points, trips) {
  const sortedTrips = [...(trips || [])].sort((a, b) =>
    String(a.startedAt || '').localeCompare(String(b.startedAt || ''))
  );
  if (!sortedTrips.length) {
    const sorted = sortPointsByTime(points);
    if (sorted.length < 2) return [];
    return [
      {
        key: 'all',
        color: TRAVEL_COLOR,
        path: sorted,
        startedAtBr: sorted[0]?.timeBr || '',
        finishedAtBr: sorted[sorted.length - 1]?.timeBr || '',
      },
    ];
  }

  return sortedTrips
    .map((trip) => {
      const key = trip.segmentKey || '';
      const tripId = trip.tripId ? String(trip.tripId) : '';
      const segPoints = sortPointsByTime(
        (points || []).filter(
          (p) =>
            (key && (p.segmentKey || '') === key) ||
            (tripId && String(p.tripId || '') === tripId)
        )
      );
      if (segPoints.length < 2 && segPoints.length === 0) return null;
      return {
        key: key || tripId || `trip-${trip.startedAt}`,
        color: trip.color || TRAVEL_COLOR,
        path: segPoints,
        startedAtBr: trip.startedAtBr || segPoints[0]?.timeBr || '',
        finishedAtBr: trip.finishedAtBr || segPoints[segPoints.length - 1]?.timeBr || '',
        operationCodes: trip.operationCodes || [],
      };
    })
    .filter(Boolean);
}

function createLabelOverlay(maps, map, { lat, lng, title, sub, tone }) {
  class LabelOverlay extends maps.OverlayView {
    constructor() {
      super();
      this.div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = `checkout-view-rastreio-pin checkout-view-rastreio-pin--${tone || 'travel'}`;
      div.innerHTML =
        `<span class="checkout-view-rastreio-pin-bubble">` +
        `<span class="checkout-view-rastreio-pin-title"></span>` +
        (sub ? `<span class="checkout-view-rastreio-pin-sub"></span>` : '') +
        `</span>` +
        `<span class="checkout-view-rastreio-pin-dot"></span>`;
      const t = div.querySelector('.checkout-view-rastreio-pin-title');
      const s = div.querySelector('.checkout-view-rastreio-pin-sub');
      if (t) t.textContent = title || '';
      if (s) s.textContent = sub || '';
      this.div = div;
      const panes = this.getPanes();
      panes?.overlayMouseTarget?.appendChild(div);
    }

    draw() {
      if (!this.div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(new maps.LatLng(lat, lng));
      if (!point) return;
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
    }

    onRemove() {
      if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  }

  const overlay = new LabelOverlay();
  overlay.setMap(map);
  return overlay;
}

/**
 * Modal de rastreio da OS no fechamento.
 */
export function CheckoutViewOsRastreioModal({ open, onClose, os, trackData, zIndex }) {
  const mapHostRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const overlays = overlaysRef;

    const clearOverlays = () => {
      (overlays.current || []).forEach((item) => {
        try {
          if (item?.setMap) item.setMap(null);
        } catch {
          /* ignore */
        }
      });
      overlays.current = [];
    };

    const paint = async () => {
      setMapError(null);
      setMapReady(false);
      try {
        const key = await fetchGoogleMapsBrowserKey();
        if (cancelled) return;
        if (!key) {
          setMapError('Google Maps não configurado');
          return;
        }
        const maps = await loadGoogleMaps(key);
        if (cancelled || !mapHostRef.current) return;

        clearOverlays();
        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapHostRef.current, {
            center: { lat: -23.55, lng: -46.63 },
            zoom: 12,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
          });
        }
        const map = mapRef.current;
        const bounds = new maps.LatLngBounds();
        let hasBounds = false;

        const travelPaths = buildTravelPaths(trackData?.points, trackData?.trips);
        for (const seg of travelPaths) {
          if (!seg.path?.length) continue;
          if (seg.path.length >= 2) {
            const poly = new maps.Polyline({
              path: seg.path.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
              geodesic: true,
              strokeColor: seg.color || TRAVEL_COLOR,
              strokeOpacity: 0.92,
              strokeWeight: 4,
              map,
            });
            overlays.current.push(poly);
          }
          const start = seg.path[0];
          const end = seg.path[seg.path.length - 1];
          if (start) {
            const pin = createLabelOverlay(maps, map, {
              lat: Number(start.lat),
              lng: Number(start.lng),
              title: 'Início deslocamento',
              sub: seg.startedAtBr || start.timeBr || '',
              tone: 'travel',
            });
            overlays.current.push(pin);
            bounds.extend({ lat: Number(start.lat), lng: Number(start.lng) });
            hasBounds = true;
          }
          if (end && seg.path.length > 1) {
            bounds.extend({ lat: Number(end.lat), lng: Number(end.lng) });
            hasBounds = true;
          }
          seg.path.forEach((p) => {
            bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) });
            hasBounds = true;
          });
        }

        const servicesList = trackData?.services || [];
        for (const svc of servicesList) {
          const sLat = Number(svc.startLat);
          const sLng = Number(svc.startLng);
          const eLat = Number(svc.endLat);
          const eLng = Number(svc.endLng);
          const hasStart = svc.startLat != null && svc.startLng != null && Number.isFinite(sLat) && Number.isFinite(sLng);
          const hasEnd = svc.endLat != null && svc.endLng != null && Number.isFinite(eLat) && Number.isFinite(eLng);
          const samePoint =
            hasStart &&
            hasEnd &&
            Math.abs(sLat - eLat) < 0.00015 &&
            Math.abs(sLng - eLng) < 0.00015;

          if (hasStart && hasEnd && samePoint) {
            const pin = createLabelOverlay(maps, map, {
              lat: sLat,
              lng: sLng,
              title: `ATENDIMENTO ${svc.operationCode || os?.numeroOS || ''}`.trim(),
              sub:
                svc.startedAtBr && svc.finishedAtBr
                  ? `${svc.startedAtBr} → ${svc.finishedAtBr}`
                  : svc.startedAtBr || svc.finishedAtBr || '',
              tone: 'service',
            });
            overlays.current.push(pin);
            bounds.extend({ lat: sLat, lng: sLng });
            hasBounds = true;
          } else {
            if (hasStart) {
              const pin = createLabelOverlay(maps, map, {
                lat: sLat,
                lng: sLng,
                title: `Início atendimento ${svc.operationCode || ''}`.trim(),
                sub: svc.startedAtBr || '',
                tone: 'service',
              });
              overlays.current.push(pin);
              bounds.extend({ lat: sLat, lng: sLng });
              hasBounds = true;
            }
            if (hasEnd) {
              const pin = createLabelOverlay(maps, map, {
                lat: eLat,
                lng: eLng,
                title: `Fim atendimento ${svc.operationCode || ''}`.trim(),
                sub: svc.finishedAtBr || '',
                tone: 'service-end',
              });
              overlays.current.push(pin);
              bounds.extend({ lat: eLat, lng: eLng });
              hasBounds = true;
            }
          }
        }

        const addr = trackData?.osAddress;
        const aLat = Number(addr?.lat);
        const aLng = Number(addr?.lng);
        if (addr && Number.isFinite(aLat) && Number.isFinite(aLng)) {
          const pin = createLabelOverlay(maps, map, {
            lat: aLat,
            lng: aLng,
            title: 'Endereço da OS',
            sub: addr.address || '',
            tone: 'address',
          });
          overlays.current.push(pin);
          bounds.extend({ lat: aLat, lng: aLng });
          hasBounds = true;
        }

        if (hasBounds) {
          map.fitBounds(bounds, 56);
        }
        setMapReady(true);
      } catch (err) {
        if (!cancelled) setMapError(err?.message || 'Erro ao carregar mapa');
      }
    };

    paint();
    return () => {
      cancelled = true;
      clearOverlays();
    };
  }, [open, trackData, os?.numeroOS]);

  if (!open) return null;

  const travels = trackData?.trips || [];
  const services = trackData?.services || [];
  const osAddress = trackData?.osAddress?.address ? trackData.osAddress : null;

  return createPortal(
    <div
      className="modal-overlay checkout-view-rastreio-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-view-rastreio-title"
      style={zIndex != null ? { zIndex } : undefined}
      onClick={() => onClose?.()}
    >
      <div className="checkout-view-rastreio-modal" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-view-rastreio-map-wrap">
          <div className="checkout-view-rastreio-topbar">
            <div className="checkout-view-rastreio-header-info">
              <h3 id="checkout-view-rastreio-title" className="checkout-view-rastreio-title">
                <Route size={16} strokeWidth={2} aria-hidden />
                Rastreio · OS {os?.numeroOS || '—'}
              </h3>
              <div className="checkout-view-rastreio-subtitle">
                <span className="checkout-view-rastreio-subtitle-line">
                  {os?.cliente || '—'}
                  {trackData?.date ? ` · ${trackData.date}` : ''}
                </span>
              </div>
              {(travels.length > 0 || services.length > 0 || osAddress) && (
                <div className="checkout-view-rastreio-segments" role="list" aria-label="Trechos do rastreio">
                  {osAddress ? (
                    <div
                      className="checkout-view-rastreio-segment checkout-view-rastreio-segment--address"
                      role="listitem"
                    >
                      <MapPin size={13} strokeWidth={2} aria-hidden />
                      <span className="checkout-view-rastreio-segment-text">
                        <span className="checkout-view-rastreio-segment-label">Endereço da OS</span>
                        <span className="checkout-view-rastreio-segment-time">{osAddress.address}</span>
                      </span>
                    </div>
                  ) : null}
                  {travels.map((travel) => (
                    <div
                      key={travel.segmentKey || travel.tripId || `${travel.startedAt}-travel`}
                      className="checkout-view-rastreio-segment checkout-view-rastreio-segment--travel"
                      role="listitem"
                      title="Deslocamento"
                    >
                      <Route size={13} strokeWidth={2} aria-hidden />
                      <span className="checkout-view-rastreio-segment-text">
                        <span className="checkout-view-rastreio-segment-label">Deslocamento</span>
                        <span className="checkout-view-rastreio-segment-time">
                          {(travel.startedAtBr || '—') + ' → ' + (travel.finishedAtBr || '—')}
                        </span>
                      </span>
                    </div>
                  ))}
                  {services.map((service) => (
                    <div
                      key={service.segmentKey || `${service.operationCode}-${service.startedAt}`}
                      className="checkout-view-rastreio-segment checkout-view-rastreio-segment--service"
                      role="listitem"
                      title="Atendimento"
                    >
                      <Wrench size={13} strokeWidth={2} aria-hidden />
                      <span className="checkout-view-rastreio-segment-text">
                        <span className="checkout-view-rastreio-segment-label">Atendimento</span>
                        <span className="checkout-view-rastreio-segment-time">
                          {(service.startedAtBr || '—') + ' → ' + (service.finishedAtBr || '—')}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="modal-close-button checkout-view-rastreio-close"
              onClick={() => onClose?.()}
              aria-label="Fechar"
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
          </div>

          {mapError ? <div className="checkout-view-rastreio-map-error">{mapError}</div> : null}
          {!mapReady && !mapError ? <div className="checkout-view-rastreio-map-loading">Carregando mapa…</div> : null}
          <div ref={mapHostRef} className="checkout-view-rastreio-map" />
        </div>
      </div>
    </div>,
    document.body
  );
}
